import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import SouthIcon from '@mui/icons-material/South';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { getStateFeeByCode, getStateNameByCode, getEntityRenewalPrice } from './dummyData';
import { sumSelectedProductPrices } from './nmiGateway';
import { useOnlineCart } from './OnlineCartContext';
import { ONLINE_CART_TOKENS } from './style';

const CALENDLY_URL = 'https://calendly.com/pcs-tax-plan/prime-business-15m';

function formatPrice(value) {
    return `$${Number(value || 0).toFixed(2)}`;
}

function createOrderNumber(seed = 'PCS') {
    const stamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `${seed}-${stamp}${random}`;
}

export default function OrderConfirmation({ answers, onConsultationBooked, onSkipConsultation }) {
    const { onlineCartRecord } = useOnlineCart();
    const cartProducts = useMemo(() => onlineCartRecord?.products || [], [onlineCartRecord]);
    const [consultationBooked, setConsultationBooked] = useState(Boolean(answers?.consultationBooked));
    const [bookedTime, setBookedTime] = useState('');
    const [orderNumber] = useState(() => answers?.orderNumber || createOrderNumber('PCS'));
    const confirmationReference =
        answers?.paymentTransactionId ||
        answers?.paymentOrderId ||
        answers?.orderSummary?.transactionId ||
        answers?.paymentChargeId ||
        answers?.orderSummary?.chargeId ||
        answers?.orderId ||
        orderNumber;

    const stateFormation = answers?.stateOfFormation || null;
    const stateCode = stateFormation?.stateCode || '';
    const stateLabel = stateFormation?.stateName || getStateNameByCode(stateCode, 'your state');
    const businessName = `${answers?.businessName?.trim() || 'Your business'} LLC`;
    const email = answers?.contactEmail || 'your email';
    const cardDigits = String(answers?.orderSummary?.cardNumber || '').replace(/\D/g, '');
    const tokenizedLast4 = String(answers?.orderSummary?.cardLast4 || '');
    const storedLast4 = String(answers?.paymentCardLast4 || '');
    const cardLast4 = storedLast4 || tokenizedLast4 || cardDigits.slice(-4) || '';
    const ssnProvided = Boolean(answers?.ssnProvided);
    const taxIdProvided = ssnProvided;
    const isExpedited = answers?.expeditedProcessing === 'expedited';
    const stateFee = Number.isFinite(Number(stateFormation?.stateFee)) ? Number(stateFormation.stateFee) : getStateFeeByCode(stateCode);
    const promoTotalDue = Number(answers?.orderSummary?.promoTotalDue);
    const promoDiscount = answers?.orderSummary?.promoApplied ? Number(answers?.orderSummary?.promoDiscount || 0) : 0;
    const serverChargedAmount = Number(answers?.totalChargeAmount);
    const hasServerChargedAmount = Number.isFinite(serverChargedAmount) && serverChargedAmount > 0;

    const dynamicProductsTotal = useMemo(() => {
        if (!Array.isArray(cartProducts) || cartProducts.length === 0) return 0;
        const summed = sumSelectedProductPrices(answers || {}, cartProducts);
        return Number.isFinite(summed) ? summed : 0;
    }, [answers, cartProducts]);

    const hasDynamicProducts = cartProducts.length > 0;
    const legacyAddons = (answers?.registeredAgent === 'add_registered_agent' ? 99 : 0) + (isExpedited ? 49 : 0);
    const computedSubtotal = getEntityRenewalPrice() + stateFee + (hasDynamicProducts ? dynamicProductsTotal : legacyAddons);

    const total = hasServerChargedAmount
        ? serverChargedAmount
        : answers?.orderSummary?.promoApplied && Number.isFinite(promoTotalDue)
          ? Math.max(0, promoTotalDue)
          : Math.max(0, computedSubtotal - promoDiscount);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        });
    }, []);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        document.head.appendChild(script);

        const handleMessage = (event) => {
            if (event?.data?.event !== 'calendly.event_scheduled') {
                return;
            }
            const startTime = event?.data?.payload?.event?.start_time;
            setConsultationBooked(true);
            if (startTime) {
                setBookedTime(
                    new Date(startTime).toLocaleString('en-US', {
                        dateStyle: 'long',
                        timeStyle: 'short',
                    })
                );
            }
            onConsultationBooked?.(startTime || '');
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onConsultationBooked]);

    const pipeline = useMemo(() => ['Review', `File with ${stateLabel}`, 'LLC Approved', 'Documents Delivered'], [stateLabel]);

    return (
        <Box data-online-cart-content sx={{ maxWidth: 672, mx: 'auto', width: '100%', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 }, fontFamily: ONLINE_CART_TOKENS.fontFamily }}>
            <Box sx={{ border: '1px solid #9CDDB4', backgroundColor: '#EAF9EF', borderRadius: 1.5, px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
                <TaskAltRoundedIcon sx={{ color: '#166534', fontSize: 20 }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#166534' }}>
                    Payment received - {formatPrice(total)} charged to card ending in {cardLast4}.
                </Typography>
            </Box>

            <Box sx={{ border: `1px solid ${ONLINE_CART_TOKENS.border}`, borderRadius: 2, backgroundColor: ONLINE_CART_TOKENS.surface, p: { xs: 2.5, sm: 4 }, mb: 4 }}>
                <Box sx={{ display: 'grid', placeItems: 'center', width: 64, height: 64, borderRadius: '50%', backgroundColor: '#E8F7ED', mx: 'auto', mb: 2 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 42, color: '#22C55E' }} />
                </Box>

                <Typography sx={{ textAlign: 'center', fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay, fontSize: { xs: '1.875rem', sm: '1.875rem' }, lineHeight: 1.2 }}>
                    {taxIdProvided ? "You're officially on your way!" : 'Order received - your filing is on hold until you verify your identity'}
                </Typography>

                <Typography sx={{ mt: 1.1, textAlign: 'center', color: ONLINE_CART_TOKENS.textSupport, fontSize: '0.875rem', lineHeight: 1.5 }}>
                    {taxIdProvided
                        ? `${businessName} formation is now in progress.`
                        : `${businessName} formation will begin once you provide your Social Security Number. Check your email for a secure link.`}
                </Typography>

                {/* <Typography sx={{ mt: 1.5, textAlign: 'center', color: ONLINE_CART_TOKENS.textMutedSoft, fontSize: '0.75rem' }}>
                    Order{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay }}>
                        {confirmationReference}
                    </Box>
                </Typography> */}

                {!taxIdProvided ? (
                    <Box sx={{ mt: 2, borderLeft: '4px solid #F59E0B', backgroundColor: '#FFFBEB', borderRadius: 1.5, px: 2, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                        <ErrorOutlineRoundedIcon sx={{ fontSize: 20, color: '#F59E0B', mt: 0.2 }} />
                        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.5, color: '#92400E' }}>
                            Your LLC filing cannot be submitted until you provide your Social Security Number. We&apos;ve sent a secure link to{' '}
                            <Box component="span" sx={{ fontWeight: 800 }}>
                                {email}
                            </Box>{' '}
                            - you can complete this step anytime.
                        </Typography>
                    </Box>
                ) : null}

                <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${ONLINE_CART_TOKENS.border}` }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', color: ONLINE_CART_TOKENS.textSupport, mb: 1.25 }}>WHAT HAPPENS NEXT</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: { xs: 'nowrap', sm: 'wrap' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: 1,
                        }}
                    >
                        {pipeline.map((step, index) => (
                            <Box key={step} sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.55,
                                        borderRadius: 10,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        textAlign: { xs: 'center', sm: 'left' },
                                        whiteSpace: 'normal',
                                        width: { xs: '100%', sm: 'auto' },
                                        backgroundColor: index === 0 ? ONLINE_CART_TOKENS.primary : '#E5E7EB',
                                        color: index === 0 ? ONLINE_CART_TOKENS.surface : '#4B5563',
                                    }}
                                >
                                    {step}
                                </Box>
                                {index < pipeline.length - 1 ? (
                                    <>
                                        <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: ONLINE_CART_TOKENS.textSupport }}>→</Typography>
                                        <Typography sx={{ display: { xs: 'block', sm: 'none' }, color: ONLINE_CART_TOKENS.textSupport }}>↓</Typography>
                                    </>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                    {isExpedited ? (
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ display: 'inline-block', px: 1.4, py: 0.45, borderRadius: 10, fontSize: '0.75rem', fontWeight: 700, backgroundColor: '#FFF1E8', color: '#DB5614' }}>
                                Expedited - submitted within 24 hrs
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                <Typography sx={{ mt: 2, textAlign: 'center', color: ONLINE_CART_TOKENS.textSupport, fontSize: '0.75rem' }}>
                    A confirmation email has been sent to{' '}
                    <Box component="span" sx={{ color: ONLINE_CART_TOKENS.textDisplay, fontWeight: 700 }}>
                        {email}
                    </Box>
                    .
                </Typography>
            </Box>

            <Box sx={{ mb: 3, display: 'grid', rowGap: 0.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1, borderTop: `1px solid ${ONLINE_CART_TOKENS.border}` }} />
                    <Typography sx={{ color: ONLINE_CART_TOKENS.primaryAccent, fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 800, textAlign: 'center' }}>
                        One last thing - claim your free tax strategy session
                    </Typography>
                    <Box sx={{ flex: 1, borderTop: `1px solid ${ONLINE_CART_TOKENS.border}` }} />
                </Box>
                <Box sx={{ mt: 1, mb: 1.5, display: 'grid', rowGap: '4px' }}>
                    <Box
                        sx={{
                            display: 'grid',
                            placeItems: 'center',
                            color: ONLINE_CART_TOKENS.primaryAccent,
                            animation: 'taxArrowBounce 1.2s ease-in-out infinite',
                            animationDelay: '0ms',
                            '@keyframes taxArrowBounce': {
                                '0%, 100%': {
                                    transform: 'translateY(0)',
                                },
                                '50%': {
                                    transform: 'translateY(8px)',
                                },
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                            },
                        }}
                    >
                        <SouthIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            placeItems: 'center',
                            color: ONLINE_CART_TOKENS.primaryAccent,
                            animation: 'taxArrowBounce 1.2s ease-in-out infinite',
                            animationDelay: '150ms',
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                            },
                        }}
                    >
                        <SouthIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            placeItems: 'center',
                            color: ONLINE_CART_TOKENS.primaryAccent,
                            animation: 'taxArrowBounce 1.2s ease-in-out infinite',
                            animationDelay: '300ms',
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                            },
                        }}
                    >
                        <SouthIcon sx={{ fontSize: 36 }} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ border: `1px solid ${ONLINE_CART_TOKENS.border}`, borderRadius: 3, backgroundColor: ONLINE_CART_TOKENS.surface, p: { xs: 2.5, sm: 3.5 } }}>
                <Typography sx={{ textAlign: 'center', fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay, fontSize: { xs: '1.5rem', sm: '1.875rem' }, lineHeight: 1.15 }}>
                    Don&apos;t Forget to Book your Complimentary Tax Strategy Call
                </Typography>
                <Typography sx={{ mt: 1.2, textAlign: 'center', color: ONLINE_CART_TOKENS.textSupport, fontSize: '0.875rem', lineHeight: 1.5 }}>
                    As a new PRIME customer, you get a complimentary 30-minute consultation with a business tax specialist. Learn how to write off 250+ deductions available to LLC owners.
                </Typography>

                <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    {[
                        { icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 19 }} />, text: 'Personalized 1-on-1 session' },
                        { icon: <PaidOutlinedIcon sx={{ fontSize: 19 }} />, text: '250+ tax deductions reviewed' },
                        { icon: <EmojiObjectsOutlinedIcon sx={{ fontSize: 19 }} />, text: 'Strategies to maximize take-home income' },
                    ].map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.1, color: ONLINE_CART_TOKENS.textSupport, fontSize: '0.875rem', fontWeight: 600 }}>
                            <Box sx={{ color: ONLINE_CART_TOKENS.primary, display: 'grid', placeItems: 'center' }}>{item.icon}</Box>
                            <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>{item.text}</Typography>
                        </Box>
                    ))}
                </Box>

                <Typography
                    sx={{ mt: 1.5, pl: 1.5, borderLeft: `2px solid ${ONLINE_CART_TOKENS.primaryBorderDashed}`, color: ONLINE_CART_TOKENS.textSupport, fontSize: '0.75rem', fontStyle: 'italic' }}
                >
                    &quot;My PRIME consultation saved me over $4,000 in my first year.&quot; - Sarah K., LLC owner
                </Typography>

                {consultationBooked ? (
                    <Box sx={{ mt: 3, p: 3, borderRadius: 2, backgroundColor: '#EAF9EF', border: '1px solid #9CDDB4', textAlign: 'center' }}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 46, color: '#16A34A' }} />
                        <Typography sx={{ mt: 1, fontWeight: 800, color: ONLINE_CART_TOKENS.textDisplay, fontSize: '1.4rem' }}>You&apos;re all set!</Typography>
                        <Typography sx={{ mt: 0.75, color: ONLINE_CART_TOKENS.textSupport, fontSize: '1.1rem' }}>
                            Your tax strategy session is booked{bookedTime ? ` for ${bookedTime}` : ''}. We&apos;ll send a calendar invite and reminder.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            className="calendly-inline-widget"
                            data-url={CALENDLY_URL}
                            sx={{
                                mt: 2.5,
                                minWidth: '320px',
                                height: { xs: 640, sm: 760 },
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                            }}
                        />
                        {/* <Button
                            variant="text"
                            onClick={onSkipConsultation}
                            sx={{
                                mt: 1.5,
                                width: '100%',
                                textTransform: 'none',
                                color: ONLINE_CART_TOKENS.textMutedSoft,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: ONLINE_CART_TOKENS.textStrong,
                                },
                            }}
                        >
                            I&apos;ll book later - go to my dashboard
                        </Button> */}
                    </>
                )}
            </Box>
        </Box>
    );
}
